/**
 * A deliberately small RFC-5545 subset: the repeat rules a family calendar actually uses
 * (see docs/DECISIONS.md, WS-4). Pure functions over local wall-clock `Date`s so the same
 * code expands occurrences in the browser and in the server-side reminder scanner.
 *
 * Supported: FREQ=DAILY|WEEKLY|MONTHLY|YEARLY, INTERVAL, BYDAY (weekly + daily filter),
 * COUNT, UNTIL. Unsupported RRULE parts are ignored rather than rejected, so a rule
 * written by a future calendar-bridge import still yields *something* sensible.
 */
export const WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
export const FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
/** Safety valve: no rule expansion ever iterates more than this many candidate dates. */
const MAX_ITERATIONS = 5000;
function isFrequency(value) {
    return FREQUENCIES.includes(value);
}
function isWeekday(value) {
    return WEEKDAYS.includes(value);
}
/**
 * `FREQ=WEEKLY;INTERVAL=2;BYDAY=MO,TH` → rule. Returns `null` for anything without a
 * usable FREQ; callers then treat the event as non-recurring instead of throwing.
 */
export function parseRRule(input) {
    if (!input)
        return null;
    const parts = new Map();
    for (const chunk of input.replace(/^RRULE:/i, '').split(';')) {
        const [rawKey, ...rest] = chunk.split('=');
        if (!rawKey || rest.length === 0)
            continue;
        parts.set(rawKey.trim().toUpperCase(), rest.join('=').trim());
    }
    const freqRaw = (parts.get('FREQ') ?? '').toUpperCase();
    if (!isFrequency(freqRaw))
        return null;
    const rule = { freq: freqRaw, interval: 1 };
    const interval = Number.parseInt(parts.get('INTERVAL') ?? '1', 10);
    if (Number.isFinite(interval) && interval > 0)
        rule.interval = interval;
    const byDay = (parts.get('BYDAY') ?? '')
        .split(',')
        .map((day) => day.trim().toUpperCase().slice(-2))
        .filter(isWeekday);
    if (byDay.length > 0)
        rule.byDay = [...new Set(byDay)];
    const count = Number.parseInt(parts.get('COUNT') ?? '', 10);
    if (Number.isFinite(count) && count > 0)
        rule.count = count;
    const until = parts.get('UNTIL');
    if (until) {
        const parsed = parseUntil(until);
        if (parsed)
            rule.until = parsed;
    }
    return rule;
}
/** Accepts both the compact RFC form (`20260930T235959Z`) and a plain ISO string. */
function parseUntil(value) {
    const compact = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z?)?$/.exec(value);
    if (compact) {
        const [, y, mo, d, h = '23', mi = '59', s = '59'] = compact;
        const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s), 0);
        return Number.isNaN(date.getTime()) ? null : date.toISOString();
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
export function formatRRule(rule) {
    const parts = [`FREQ=${rule.freq}`];
    if (rule.interval > 1)
        parts.push(`INTERVAL=${rule.interval}`);
    if (rule.byDay && rule.byDay.length > 0)
        parts.push(`BYDAY=${rule.byDay.join(',')}`);
    if (rule.count)
        parts.push(`COUNT=${rule.count}`);
    if (rule.until)
        parts.push(`UNTIL=${compactUntil(rule.until)}`);
    return parts.join(';');
}
function compactUntil(iso) {
    return `${iso
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}/, '')
        .replace(/Z$/, '')}Z`;
}
const DAY_LABELS = {
    SU: 'Sun',
    MO: 'Mon',
    TU: 'Tue',
    WE: 'Wed',
    TH: 'Thu',
    FR: 'Fri',
    SA: 'Sat',
};
/** Human summary for the form/detail sheet: "Weekly on Thu", "Every 2 weeks on Mon, Thu". */
export function describeRRule(rule) {
    if (!rule)
        return 'Does not repeat';
    const days = rule.byDay?.map((day) => DAY_LABELS[day]).join(', ');
    const every = rule.interval > 1 ? `Every ${rule.interval} ` : '';
    let base;
    switch (rule.freq) {
        case 'DAILY':
            base = rule.interval > 1 ? `${every}days` : 'Daily';
            break;
        case 'WEEKLY':
            base = rule.interval > 1 ? `${every}weeks` : 'Weekly';
            if (days)
                base += ` on ${days}`;
            break;
        case 'MONTHLY':
            base = rule.interval > 1 ? `${every}months` : 'Monthly';
            break;
        default:
            base = rule.interval > 1 ? `${every}years` : 'Yearly';
            break;
    }
    if (rule.count)
        base += ` · ${rule.count} times`;
    else if (rule.until)
        base += ` · until ${new Date(rule.until).toLocaleDateString()}`;
    return base;
}
function startOfDay(date) {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
}
function withTimeOf(day, source) {
    const copy = new Date(day);
    copy.setHours(source.getHours(), source.getMinutes(), source.getSeconds(), source.getMilliseconds());
    return copy;
}
function addDays(date, days) {
    const copy = new Date(date);
    copy.setDate(copy.getDate() + days);
    return copy;
}
/**
 * Occurrence start times for `rule` anchored at `dtstart`, in ascending order.
 *
 * COUNT/UNTIL are evaluated over the *whole* series, not the requested window, so paging
 * through months never resurrects an occurrence past the end of the series.
 */
export function expandRule(rule, dtstart, options = {}) {
    const limit = options.limit ?? 500;
    const from = options.from ?? Number.NEGATIVE_INFINITY;
    const to = options.to ?? Number.POSITIVE_INFINITY;
    const untilMs = rule.until ? new Date(rule.until).getTime() : Number.POSITIVE_INFINITY;
    const out = [];
    let emitted = 0;
    let iterations = 0;
    const consider = (candidate) => {
        const ms = candidate.getTime();
        if (ms < dtstart.getTime())
            return 'continue';
        if (ms > untilMs)
            return 'stop';
        if (rule.count !== undefined && emitted >= rule.count)
            return 'stop';
        emitted += 1;
        if (ms >= from && ms <= to) {
            out.push(candidate);
            if (out.length >= limit)
                return 'stop';
        }
        if (ms > to)
            return 'stop';
        return 'continue';
    };
    if (rule.freq === 'WEEKLY') {
        const dayNumbers = (rule.byDay ? [...new Set(rule.byDay.map((day) => WEEKDAYS.indexOf(day)))] : [dtstart.getDay()]).sort((a, b) => a - b);
        // Anchor on the Monday of dtstart's week (WKST=MO, the RFC default).
        const anchor = startOfDay(addDays(dtstart, -((dtstart.getDay() + 6) % 7)));
        for (let week = 0; iterations < MAX_ITERATIONS; week += 1) {
            const weekStart = addDays(anchor, week * 7 * rule.interval);
            for (const dayNumber of dayNumbers) {
                iterations += 1;
                // weekStart is a Monday; MO=1 … SU=0 maps onto offsets 0…6.
                const offset = (dayNumber + 6) % 7;
                const candidate = withTimeOf(addDays(weekStart, offset), dtstart);
                if (consider(candidate) === 'stop')
                    return out;
            }
        }
        return out;
    }
    if (rule.freq === 'DAILY') {
        const allowed = rule.byDay ? new Set(rule.byDay.map((day) => WEEKDAYS.indexOf(day))) : null;
        for (let step = 0; iterations < MAX_ITERATIONS; step += 1) {
            iterations += 1;
            const candidate = withTimeOf(addDays(dtstart, step * rule.interval), dtstart);
            if (allowed && !allowed.has(candidate.getDay())) {
                if (candidate.getTime() > to)
                    return out;
                continue;
            }
            if (consider(candidate) === 'stop')
                return out;
        }
        return out;
    }
    const monthly = rule.freq === 'MONTHLY';
    const dayOfMonth = dtstart.getDate();
    const anchorMonth = dtstart.getFullYear() * 12 + dtstart.getMonth();
    for (let step = 0; iterations < MAX_ITERATIONS; step += 1) {
        iterations += 1;
        const monthIndex = anchorMonth + step * rule.interval * (monthly ? 1 : 12);
        const year = Math.floor(monthIndex / 12);
        const month = monthIndex % 12;
        // RFC 5545: a month without the anchor day (Feb 30, Feb 29 in a common year) is skipped.
        const candidate = withTimeOf(new Date(year, month, dayOfMonth), dtstart);
        if (candidate.getMonth() !== month) {
            if (candidate.getTime() > to)
                return out;
            continue;
        }
        if (consider(candidate) === 'stop')
            return out;
    }
    return out;
}
//# sourceMappingURL=recurrence.js.map